/**
 * Host identity helpers — mirrors mobile LiveHostInfo / stream preview builders.
 */

export function isPageHost(hostInfo) {
  return hostInfo?.type === 'page'
}

export function buildHostInfoFromStream(stream) {
  if (!stream) return null
  if (stream.pageId || stream.page) {
    const page = stream.page || {}
    return {
      type: 'page',
      id: page.id || stream.pageId,
      name: page.name,
      username: page.username,
      profilePictureUrl: page.profilePictureUrl,
      isFollowing: false,
    }
  }
  const user = stream.user
  if (user) {
    return {
      type: 'user',
      id: user.id,
      username: user.username,
      firstName: user.firstName,
      lastName: user.lastName,
      profilePictureUrl: user.profilePictureUrl,
      isFriend: false,
      isSelf: false,
    }
  }
  return null
}

export function getHostDisplayName({ hostInfo, stream, followingItem } = {}) {
  if (hostInfo) {
    if (hostInfo.type === 'page') {
      const name = hostInfo.name || hostInfo.username || ''
      if (name) return name
      // Partial join payloads may omit page name — fall through to stream/following.
    } else {
      const first = (hostInfo.firstName || '').trim()
      const last = (hostInfo.lastName || '').trim()
      if (first || last) return `${first} ${last}`.trim()
      return hostInfo.username || hostInfo.displayName || ''
    }
  }
  if (stream?.page) return stream.page.name || stream.page.username || ''
  if (stream?.pageId && !stream?.user) return followingItem?.displayName || stream.title || ''
  if (followingItem?.pageId) return followingItem.displayName || ''
  const user = stream?.user
  if (user) {
    const first = (user.firstName || '').trim()
    const last = (user.lastName || '').trim()
    if (first || last) return `${first} ${last}`.trim()
    return user.username || ''
  }
  if (followingItem) return followingItem.displayName || followingItem.username || ''
  return ''
}

export function getHostAvatarUrl({ hostInfo, stream, followingItem } = {}) {
  if (hostInfo?.profilePictureUrl) return hostInfo.profilePictureUrl
  if (stream?.page?.profilePictureUrl) return stream.page.profilePictureUrl
  if (followingItem?.avatarUrl) return followingItem.avatarUrl
  return stream?.user?.profilePictureUrl || null
}

export function getHostSubtitle({ hostInfo, stream } = {}) {
  if (isPageHost(hostInfo)) {
    return hostInfo.username ? `@${hostInfo.username}` : ''
  }
  if (stream?.page?.username) return `@${stream.page.username}`
  const username = hostInfo?.username || stream?.user?.username
  return username ? `@${username}` : ''
}

export function hostShowActionButton(hostInfo) {
  if (!hostInfo) return false
  if (hostInfo.isSelf) return false
  if (hostInfo.type === 'page') return !hostInfo.isFollowing
  if (hostInfo.type === 'user') return !hostInfo.isFriend
  return false
}

export function getHostActionId(hostInfo) {
  if (!hostInfo) return null
  return hostInfo.id || hostInfo.userId || null
}

export function resolveViewerHostDisplay(liveStore) {
  const stream = liveStore.liveStream
  const hostInfo = liveStore.hostInfo
  const followingItem = liveStore.followingLiveItems?.find(
    (item) => item.liveStreamId === stream?.id,
  )
  const displayName = getHostDisplayName({ hostInfo, stream, followingItem })
  const profilePictureUrl = getHostAvatarUrl({ hostInfo, stream, followingItem })
  const subtitle = getHostSubtitle({ hostInfo, stream })
  const isPage = isPageHost(hostInfo) || !!stream?.pageId
  return {
    displayName,
    profilePictureUrl,
    subtitle,
    isPage,
    hostInfo,
  }
}

export function resolveHostBroadcastDisplay(liveStore, currentUser) {
  if (liveStore.postAsPageId) {
    return {
      displayName: liveStore.postAsPageName,
      profilePictureUrl: liveStore.postAsPageAvatar,
      isPage: true,
    }
  }
  if (liveStore.liveStream?.page) {
    return {
      displayName: liveStore.liveStream.page.name || liveStore.liveStream.page.username,
      profilePictureUrl: liveStore.liveStream.page.profilePictureUrl,
      isPage: true,
    }
  }
  if (liveStore.liveStream?.pageId) {
    return {
      displayName: liveStore.postAsPageName || liveStore.liveStream.title || '',
      profilePictureUrl: liveStore.postAsPageAvatar || null,
      isPage: true,
    }
  }
  const user = currentUser || liveStore.liveStream?.user
  const first = (user?.firstName || '').trim()
  const last = (user?.lastName || '').trim()
  return {
    displayName: (first || last) ? `${first} ${last}`.trim() : (user?.username || ''),
    profilePictureUrl: user?.profilePictureUrl || null,
    isPage: false,
  }
}

export function getStreamCardHost(stream) {
  if (stream?.pageId || stream?.page) {
    const page = stream.page || {}
    return {
      name: page.name || page.username || stream.title || '',
      avatarUrl: page.profilePictureUrl || null,
      isPage: true,
    }
  }
  const user = stream?.user
  return {
    name: user?.username || user?.firstName || '',
    avatarUrl: user?.profilePictureUrl || null,
    isPage: false,
  }
}

export function isPageCommentAuthor(comment) {
  return !!(comment?.pageId || comment?.page?.id || comment?.page?.name)
}

export function getCommentAuthorName(comment) {
  const pageName = comment?.page?.name?.trim()
  if (pageName) return pageName
  if (comment?.pageId && !comment?.user?.username) return comment?.page?.username?.trim() || 'Page'
  const user = comment?.user
  if (user?.username) return user.username
  const first = (user?.firstName || '').trim()
  const last = (user?.lastName || '').trim()
  if (first || last) return `${first} ${last}`.trim()
  return ''
}

export function getCommentAuthorAvatar(comment) {
  return comment?.page?.profilePictureUrl || comment?.user?.profilePictureUrl || null
}

function normalizeViewerRole(role) {
  const key = String(role || 'VIEWER').toUpperCase()
  if (key === 'MODERATOR') return 'moderator'
  if (key === 'GUEST') return 'guest'
  return 'viewer'
}

export function getViewerUserId(viewer) {
  if (!viewer) return null
  return viewer.userId || viewer.user?.id || viewer.id || null
}

export function getViewerDisplayName(viewer) {
  if (!viewer) return ''
  if (viewer.displayName) return viewer.displayName
  const user = viewer.user || viewer
  const first = (user.firstName || '').trim()
  const last = (user.lastName || '').trim()
  if (first || last) return `${first} ${last}`.trim()
  return user.username || viewer.username || ''
}

export function getViewerAvatarUrl(viewer) {
  if (!viewer) return null
  return viewer.profilePictureUrl || viewer.user?.profilePictureUrl || null
}

export function normalizeViewerListItem(entry) {
  if (!entry) return entry
  const user = entry.user || {}
  const userId = getViewerUserId(entry)
  return {
    ...entry,
    userId,
    id: userId || entry.id,
    username: user.username || entry.username || '',
    displayName: getViewerDisplayName(entry),
    profilePictureUrl: getViewerAvatarUrl(entry),
    role: normalizeViewerRole(entry.role),
    user: Object.keys(user).length ? user : entry.user,
  }
}

export function buildUserProfileHref(userId, router) {
  if (!userId) return null
  if (router?.resolve) {
    return router.resolve({ name: 'social.friendprofile', params: { id: userId } }).href
  }
  const base = (import.meta.env?.BASE_URL || process.env.BASE_URL || '/').replace(/\/$/, '')
  return `${base}/friendprofile/${userId}`
}
